import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { CompanyProfile, fetchPublicCompanyProfile } from '@/services/companyProfile';
import { BookingAvailabilityDate, BookingLocation, fetchBookingAvailability } from '@/services/bookingAvailability';
import CalendarPicker from './components/CalendarPicker';
import TimeSlotPicker from './components/TimeSlotPicker';
import StepProgress from './components/StepProgress';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay * 80}ms` }}
    >
      {children}
    </div>
  );
}

const STEPS = [
  { label: '選擇日期', icon: 'ri-calendar-line' },
  { label: '填寫資料', icon: 'ri-user-line' },
  { label: '確認預約', icon: 'ri-check-line' },
];

const benefits = [
  { icon: 'ri-check-double-line', title: '免費專業檢測', desc: '由國家認證聽力師進行完整聽力評估，不收取任何費用。' },
  { icon: 'ri-time-line', title: '彈性預約時段', desc: '提供多個時段選擇，配合您的行程安排，節省等待時間。' },
  { icon: 'ri-user-heart-line', title: '個人化建議', desc: '根據檢測結果，提供專屬的聽力保健方案與助聽器選配建議。' },
  { icon: 'ri-gift-line', title: '預約專屬優惠', desc: '線上預約完成檢測後，可享助聽器選配折扣與延長保固。' },
];

const fallbackCompanyProfile: Pick<CompanyProfile, 'servicePhone' | 'servicePhoneNote' | 'serviceHours' | 'serviceEmail' | 'addressCity' | 'addressDistrict' | 'addressLine'> = {
  servicePhone: '0800-123-456',
  servicePhoneNote: '免付費',
  serviceHours: '週一至週五 09:00 - 18:00',
  serviceEmail: 'service@fuling.com',
  addressCity: '台北市',
  addressDistrict: '大安區',
  addressLine: '忠孝東路四段 100 號',
};

function buildContactCards(profile: typeof fallbackCompanyProfile) {
  return [
    { icon: 'ri-phone-line', title: '客服專線', content: `${profile.servicePhone}${profile.servicePhoneNote ? `（${profile.servicePhoneNote}）` : ''}`, sub: profile.serviceHours },
    { icon: 'ri-mail-line', title: '電子郵件', content: profile.serviceEmail, sub: '我們將於 24 小時內回覆' },
    { icon: 'ri-map-pin-line', title: '公司地址', content: `${profile.addressCity}${profile.addressDistrict}${profile.addressLine}`, sub: '' },
  ];
}

function formatDateValue(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default function Booking() {
  const [searchParams] = useSearchParams();
  const bookingType = 'ProductTrial';
  const selectedProduct = {
    id: searchParams.get('productId') || '',
    name: searchParams.get('productName') || '官網預約試戴',
    code: searchParams.get('productCode') || '',
  };
  const hasSelectedProduct = Boolean(selectedProduct.id || selectedProduct.name);
  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({ name: '', phone: '', store: '', description: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(fallbackCompanyProfile);
  const [bookingLocations, setBookingLocations] = useState<BookingLocation[]>([]);
  const [availabilityDates, setAvailabilityDates] = useState<BookingAvailabilityDate[]>([]);
  const [bookingPublicLookaheadDays, setBookingPublicLookaheadDays] = useState(180);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState('');
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const availabilityStartDate = formatDateValue(today);

  useEffect(() => {
    let alive = true;
    fetchPublicCompanyProfile().then((profile) => {
      if (alive && profile) setCompanyProfile(profile);
    });
    return () => {
      alive = false;
    };
  }, []);

  async function loadAvailability(storeName: string) {
    setAvailabilityLoading(true);
    setAvailabilityError('');
    try {
      const data = await fetchBookingAvailability({
        Store: storeName,
        StartDate: availabilityStartDate,
      });
      setBookingLocations(data.Locations);
      setAvailabilityDates(data.Dates);
      setBookingPublicLookaheadDays(data.BookingPublicLookaheadDays || 180);
      if (!storeName && data.Locations[0]) {
        setFormData((current) => ({ ...current, store: data.Locations[0].Name }));
      }
    } catch (error) {
      setAvailabilityError(error instanceof Error ? error.message : '可預約時段載入失敗。');
    } finally {
      setAvailabilityLoading(false);
    }
  }

  useEffect(() => {
    void loadAvailability('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactCards = buildContactCards(companyProfile);
  const availabilityByDate = useMemo(() => {
    const map: Record<string, { isOpen: boolean; hasAvailableSlots: boolean; isNationalHoliday?: boolean; note?: string | null }> = {};
    availabilityDates.forEach((item) => {
      map[item.AppointmentDate] = {
        isOpen: item.IsOpen,
        hasAvailableSlots: item.Slots.some((slot) => slot.Available),
        isNationalHoliday: item.IsNationalHoliday,
        note: item.Note,
      };
    });
    return map;
  }, [availabilityDates]);
  const selectedAvailability = availabilityDates.find((item) => item.AppointmentDate === selectedDate);
  const selectedSlots = selectedAvailability?.Slots || [];

  const validateStep0 = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.store.trim()) newErrors.store = '請選擇希望前往門市';
    if (!selectedDate) newErrors.date = '請選擇預約日期';
    if (!selectedTime) newErrors.time = '請選擇預約時段';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = '請輸入姓名';
    if (!formData.phone.trim()) {
      newErrors.phone = '請輸入聯絡電話';
    } else if (!/^[0-9\-]{8,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = '請輸入有效的電話號碼';
    }
    if (!formData.store.trim()) newErrors.store = '請選擇希望前往門市';
    if (!formData.description.trim()) newErrors.description = bookingType === 'ProductTrial' ? '請補充試戴需求或聽力狀況' : '請簡述您的聽力問題';
    else if (formData.description.length > 500) newErrors.description = '描述請勿超過 500 字';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let valid = true;
    if (step === 0) valid = validateStep0();
    if (step === 1) valid = validateStep1();
    if (!valid) {
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-400');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    setErrors({});
    setSubmitStatus('idle');
    setStep((s) => s + 1);
    document.getElementById('booking-step')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBack = () => {
    setErrors({});
    setSubmitStatus('idle');
    setStep((s) => s - 1);
    document.getElementById('booking-step')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedTime('');
    if (errors.date) {
      setErrors((prev) => { const n = { ...prev }; delete n.date; return n; });
    }
    setTimeout(() => {
      document.getElementById('time-slot-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (errors.time) {
      setErrors((prev) => { const n = { ...prev }; delete n.time; return n; });
    }
  };

  const handleStoreSelect = (store: string) => {
    setFormData((prev) => ({ ...prev, store }));
    setSelectedDate('');
    setSelectedTime('');
    setErrors((prev) => {
      const next = { ...prev };
      delete next.store;
      delete next.date;
      delete next.time;
      return next;
    });
    void loadAvailability(store);
  };

  const scrollInputIntoView = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    setTimeout(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'store') {
      handleStoreSelect(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep1()) return;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/product-trial-reservations', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          Name: formData.name.trim(),
          Phone: formData.phone.trim(),
          Store: formData.store,
          ReservationType: bookingType,
          AppointmentDate: selectedDate,
          AppointmentTime: selectedTime,
          ContactTime: selectedTime,
          ProductId: selectedProduct.id,
          ProductName: selectedProduct.name,
          ProductCode: selectedProduct.code,
          Note: formData.description.trim(),
          Campaign: 'ProductTrial',
          PageUrl: window.location.href,
        }),
      });

      if (response.ok) {
        const params = new URLSearchParams();
        params.append('name', formData.name);
        params.append('phone', formData.phone);
        params.append('date', selectedDate);
        params.append('time', selectedTime);
        params.append('store', formData.store);
        params.append('description', formData.description);
        params.append('type', bookingType);
        if (selectedProduct.id) params.append('productId', selectedProduct.id);
        if (selectedProduct.name) params.append('productName', selectedProduct.name);
        if (selectedProduct.code) params.append('productCode', selectedProduct.code);
        navigate(`/booking-success?${params.toString()}`);
      } else {
        const data = await response.json().catch(() => ({}));
        const apiErrors = data.errors && typeof data.errors === 'object' ? Object.values(data.errors).filter(Boolean) : [];
        setSubmitError(data.message || apiErrors[0] as string || '');
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDisplayDate = (d: string) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${y} 年 ${m} 月 ${day} 日`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* Hero — 聽力檢測預約 */}
        <div className="relative overflow-hidden text-white pt-4 md:pt-6 pb-8 md:pb-12">
          {/* 深色基底 */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900 to-primary-950" />

          {/* 上方琥珀光暈 */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full bg-accent-500/18 blur-[140px]" />
          {/* 左下綠色光池 */}
          <div className="absolute -bottom-20 -left-10 w-[350px] h-[350px] md:w-[500px] md:h-[500px] rounded-full bg-primary-600/20 blur-[110px]" />
          {/* 中央高光 */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-60 md:h-60 rounded-full bg-accent-400/10 blur-[90px]" />
          {/* 右下點綴 */}
          <div className="absolute bottom-10 right-8 w-28 h-28 md:w-44 md:h-44 rounded-full bg-primary-500/15 blur-3xl" />
          {/* 左側上方點綴 */}
          <div className="absolute top-20 left-10 w-20 h-20 md:w-32 md:h-32 rounded-full bg-accent-600/12 blur-3xl" />

          {/* 網格紋理 */}
          <div className="absolute inset-0 opacity-[0.025]">
            <svg width="100%" height="100%">
              <defs>
                <pattern id="booking-hero-grid" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
                  <circle cx="24" cy="24" r="0.8" fill="white" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#booking-hero-grid)" />
            </svg>
          </div>

          {/* 音律波形裝飾 */}
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
            <svg viewBox="0 0 1440 360" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" fill="none">
              <path
                d="M0 180 Q30 155 60 165 Q90 175 120 150 Q150 125 180 145 Q210 165 240 140 Q270 115 300 135 Q330 155 360 130 Q390 105 420 125 Q450 145 480 155 Q510 165 540 145 Q570 125 600 140 Q630 155 660 135 Q690 115 720 130 Q750 145 780 155 Q810 165 840 145 Q870 125 900 138 Q930 151 960 130 Q990 109 1020 125 Q1050 141 1080 120 Q1110 99 1140 118 Q1170 137 1200 125 Q1230 113 1260 130 Q1290 147 1320 135 Q1350 123 1380 138 Q1410 153 1440 140"
                stroke="white"
                strokeWidth="2.5"
                opacity="0.7"
              />
              <path
                d="M0 210 Q25 200 50 208 Q75 216 100 205 Q125 194 150 203 Q175 212 200 198 Q225 184 250 195 Q275 206 300 192 Q325 178 350 189 Q375 200 400 186 Q425 172 450 184 Q475 196 500 182 Q525 168 550 180 Q575 192 600 178 Q625 164 650 176 Q675 188 700 174 Q725 160 750 172 Q775 184 800 170 Q825 156 850 168 Q875 180 900 166 Q925 152 950 164 Q975 176 1000 162 Q1025 148 1050 160 Q1075 172 1100 158 Q1125 144 1150 156 Q1175 168 1200 154 Q1225 140 1250 152 Q1275 164 1300 150 Q1325 136 1350 148 Q1375 160 1400 148 Q1425 136 1440 145"
                stroke="white"
                strokeWidth="1.2"
                opacity="0.4"
                strokeDasharray="10 6"
              />
              <path
                d="M0 240 L20 232 L40 244 L60 230 L80 242 L100 228 L120 240 L140 226 L160 238 L180 224 L200 236 L220 222 L240 234 L260 220 L280 232 L300 218 L320 230 L340 216 L360 228 L380 214 L400 226 L420 212 L440 224 L460 210 L480 222 L500 208 L520 220 L540 206 L560 218 L580 204 L600 216 L620 202 L640 214 L660 200 L680 212 L700 198 L720 210 L740 196 L760 208 L780 194 L800 206 L820 192 L840 204 L860 190 L880 202 L900 188 L920 200 L940 186 L960 198 L980 184 L1000 196 L1020 182 L1040 194 L1060 180 L1080 192 L1100 178 L1120 190 L1140 176 L1160 188 L1180 174 L1200 186 L1220 172 L1240 184 L1260 170 L1280 182 L1300 168 L1320 180 L1340 166 L1360 178 L1380 164 L1400 176 L1420 162 L1440 172"
                stroke="white"
                strokeWidth="0.7"
                opacity="0.3"
              />
            </svg>
          </div>

          {/* 漂浮粒子 */}
          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center pt-10 md:pt-14 pb-8 md:pb-10">
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {Array.from({ length: 8 }, (_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full animate-float"
                  style={{
                    left: `${10 + (i * 10) % 80}%`,
                    top: `${10 + (i * 15) % 80}%`,
                    width: `${3 + (i % 3) * 2}px`,
                    height: `${3 + (i % 3) * 2}px`,
                    animationDelay: `${i * 0.8}s`,
                    animationDuration: `${4 + (i % 2) * 3}s`,
                    backgroundColor: i % 3 === 0 ? 'rgba(255,255,255,0.25)' : i % 3 === 1 ? 'rgba(212,165,116,0.2)' : 'rgba(46,139,87,0.18)',
                  }}
                />
              ))}
            </div>

            <div className="relative">
              <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
                <div className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-accent-300" />
                </div>
                <span className="text-xs font-semibold tracking-[0.25em] text-accent-300 uppercase">
                  預約服務
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                {bookingType === 'ProductTrial' ? '預約' : '預約'}<span className="text-accent-300">{bookingType === 'ProductTrial' ? '商品試戴' : '聽力檢測'}</span>
              </h1>
              <p className="mt-4 md:mt-5 text-white/75 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                {bookingType === 'ProductTrial'
                  ? '填寫以下資料，我們將依您選擇的商品安排門市試戴與專業諮詢。'
                  : '填寫以下資料，我們的專業聽力師將為您安排免費的聽力檢測與諮詢服務。'}
              </p>

              {/* 服務亮點標籤 */}
              <div className="mt-5 md:mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-3">
                {(bookingType === 'ProductTrial' ? ['商品試戴', '一對一諮詢', '門市體驗', '彈性預約', '專屬建議'] : ['免費檢測', '一對一諮詢', '國家認證聽力師', '彈性預約', '專屬優惠']).map((tag) => (
                  <span
                    key={tag}
                    className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/8 text-white/85 border border-white/15 backdrop-blur-sm hover:bg-white/14 hover:border-white/25 transition-all duration-300 cursor-default"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>

        {/* Step Progress */}
        <div id="booking-step" className="px-6 md:px-10 lg:px-16 pt-6 md:pt-8 max-w-7xl mx-auto scroll-mt-20">
          <div className="max-w-3xl mx-auto">
            <StepProgress currentStep={step} steps={STEPS} />
          </div>
          {hasSelectedProduct && (
            <div className="mx-auto mt-4 max-w-3xl rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1B3D2A]/10">
                  <i className="ri-hearing-line text-[#1B3D2A]" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold tracking-wider text-[#D4A574]">{bookingType === 'ProductTrial' ? '商品試戴來源' : '商品頁預約來源'}</p>
                  <p className="truncate text-base font-bold text-[#1B3D2A]">{selectedProduct.name || '已選擇商品'}</p>
                  {selectedProduct.code && <p className="mt-0.5 text-xs text-[#9A8A7A]">產品編號：{selectedProduct.code}</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="px-6 md:px-10 lg:px-16 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-12">
            {/* Left - Info */}
            <div className="lg:w-5/12 flex flex-col gap-4 md:gap-5">
              <SectionReveal>
                <div>
                  <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                    預約好處
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">為什麼要預約聽力檢測？</h2>
                  <div className="w-12 h-1 bg-[#D4A574] mt-3 rounded-full" />
                </div>
              </SectionReveal>

              <SectionReveal>
                <div className="relative rounded-2xl overflow-hidden h-48 md:h-56 border border-[#E8E0D6] group">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20audiologist%20performing%20hearing%20test%20for%20elderly%20patient%20in%20modern%20warm%20clinic%20room%20with%20soft%20green%20and%20cream%20tones%20professional%20medical%20equipment%20caring%20atmosphere%20natural%20lighting%20high%20quality%20photography&width=600&height=400&seq=30&orientation=landscape"
                    alt="專業聽力檢測諮詢"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white text-sm font-medium">專業聽力師一對一檢測服務</p>
                  </div>
                </div>
              </SectionReveal>

              <div className="space-y-2.5">
                {benefits.map((item, idx) => (
                  <SectionReveal key={item.title} delay={idx}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-[#1B3D2A]`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-[#1B3D2A]">{item.title}</h3>
                        <p className="text-sm text-[#5A6B5A] mt-0.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:w-7/12">
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-4 md:p-6 lg:p-8">
                {/* Step 0: Date & Time */}
                {step === 0 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">選擇預約日期</h2>
                      <p className="mt-2 text-sm text-[#5A6B5A]">請選擇您方便前來的日期與時段</p>
                    </div>

                    <label className="grid gap-2 text-sm font-bold text-[#1B3D2A]">
                      <span>希望前往門市 <span className="text-[#D4A574]">*</span></span>
                      <select
                        className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#1B3D2A]/15 ${
                          errors.store ? 'border-red-400 bg-red-50' : 'border-[#E8E0D6] bg-white focus:border-[#1B3D2A]'
                        }`}
                        disabled={availabilityLoading || bookingLocations.length === 0}
                        name="store"
                        value={formData.store}
                        onChange={handleChange}
                      >
                        <option value="">請選擇門市</option>
                        {bookingLocations.map((location) => <option key={location.LocationId} value={location.Name}>{location.Name}</option>)}
                      </select>
                      {errors.store && <p className="text-sm text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" /> {errors.store}</p>}
                      {availabilityError && <p className="text-sm text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" /> {availabilityError}</p>}
                    </label>

                    <div className="bg-[#FAF8F5] rounded-xl p-4 md:p-6">
                      <p className="mb-3 text-xs font-medium text-[#9A8A7A]">目前開放未來 {bookingPublicLookaheadDays} 天內預約，實際可選日期依門市設定與剩餘時段為準。</p>
                      <CalendarPicker availability={availabilityByDate} loading={availabilityLoading} selectedDate={selectedDate} onSelect={handleDateSelect} />
                    </div>
                    {errors.date && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <i className="ri-error-warning-line" /> {errors.date}
                      </p>
                    )}

                    <div id="time-slot-section">
                      <h3 className="text-sm font-bold text-[#1B3D2A] mb-3">選擇時段</h3>
                      <TimeSlotPicker loading={availabilityLoading} selectedTime={selectedTime} slots={selectedSlots} onSelect={handleTimeSelect} />
                      {errors.time && (
                        <p className="text-sm text-red-500 flex items-center gap-1 mt-2">
                          <i className="ri-error-warning-line" /> {errors.time}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={handleNext}
                        className="bg-[#1B3D2A] text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors flex items-center gap-2"
                      >
                        下一步
                        <i className="ri-arrow-right-line" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 1: Personal Info */}
                {step === 1 && (
                  <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">填寫預約資料</h2>
                      <p className="mt-2 text-sm text-[#5A6B5A]">請提供您的基本資訊，我們將盡快與您聯繫確認</p>
                    </div>

                    {/* Selected summary */}
                    <div className="bg-[#FAF8F5] rounded-xl p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <i className="ri-calendar-check-line text-[#1B3D2A]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1B3D2A]">
                          {formatDisplayDate(selectedDate)} {selectedTime}
                        </p>
                        <button
                          type="button"
                          onClick={handleBack}
                          className="text-xs text-[#D4A574] hover:text-[#C49464] underline transition-colors"
                        >
                          修改日期
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">
                        姓名 <span className="text-[#D4A574]">*</span>
                      </label>
                      <input
                        type="text" id="name" name="name" value={formData.name}
                        onChange={handleChange}
                        onFocus={scrollInputIntoView}
                        autoComplete="name"
                        placeholder="請輸入您的姓名"
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 ${
                          errors.name ? 'border-red-400 bg-red-50' : 'border-[#E8E0D6] bg-white focus:border-[#1B3D2A]'
                        }`}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line" /> {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">
                        聯絡電話 <span className="text-[#D4A574]">*</span>
                      </label>
                      <input
                        type="tel" id="phone" name="phone" value={formData.phone}
                        onChange={handleChange}
                        onFocus={scrollInputIntoView}
                        autoComplete="tel"
                        placeholder="例如：0912-345-678"
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 ${
                          errors.phone ? 'border-red-400 bg-red-50' : 'border-[#E8E0D6] bg-white focus:border-[#1B3D2A]'
                        }`}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line" /> {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">
                        希望前往門市 <span className="text-[#D4A574]">*</span>
                      </label>
                      <div className="rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] px-4 py-3 text-sm font-bold text-[#1B3D2A]">
                        {formData.store || '尚未選擇門市'}
                      </div>
                      <button
                        type="button"
                        onClick={() => setStep(0)}
                        className="mt-1 text-xs text-[#D4A574] hover:text-[#C49464] underline transition-colors"
                      >
                        修改門市與日期
                      </button>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">
                        {bookingType === 'ProductTrial' ? '試戴需求與聽力狀況' : '聽力問題描述'} <span className="text-[#D4A574]">*</span>
                      </label>
                      <textarea
                        id="description" name="description" value={formData.description}
                        onChange={handleChange}
                        onFocus={scrollInputIntoView}
                        autoComplete="off"
                        placeholder={bookingType === 'ProductTrial' ? '可補充想試戴的情境、目前配戴經驗或聽力困擾...' : '請簡述您的聽力狀況或困擾...'}
                        rows={4}
                        maxLength={500}
                        className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 resize-none ${
                          errors.description ? 'border-red-400 bg-red-50' : 'border-[#E8E0D6] bg-white focus:border-[#1B3D2A]'
                        }`}
                      />
                      <div className="flex items-center justify-between mt-1">
                        {errors.description ? (
                          <p className="text-sm text-red-500 flex items-center gap-1">
                            <i className="ri-error-warning-line" /> {errors.description}
                          </p>
                        ) : <span />}
                        <span className={`text-xs ${formData.description.length > 500 ? 'text-red-500' : 'text-[#9A8A7A]'}`}>
                          {formData.description.length} / 500
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="px-6 py-2.5 rounded-xl text-sm font-medium border border-[#E8E0D6] text-[#5A6B5A] hover:bg-[#FAF8F5] transition-colors flex items-center gap-2"
                      >
                        <i className="ri-arrow-left-line" />
                        上一步
                      </button>
                      <div className="flex-1" />
                      <button
                        type="submit"
                        className="bg-[#1B3D2A] text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors flex items-center gap-2"
                      >
                        下一步
                        <i className="ri-arrow-right-line" />
                      </button>
                    </div>
                  </form>
                )}

                {/* Step 2: Confirm */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">確認預約資訊</h2>
                      <p className="mt-2 text-sm text-[#5A6B5A]">請確認以下資訊是否正確，確認後即可提交預約</p>
                    </div>

                    {submitStatus === 'error' && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                          <i className="ri-error-warning-line text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-red-800">提交失敗</p>
                          <p className="text-sm text-red-600 mt-1">
                            {submitError || `網路連線異常，請稍後再試，或撥打客服專線 ${companyProfile.servicePhone} 直接預約。`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Summary card */}
                    <div className="bg-[#FAF8F5] rounded-xl p-4 md:p-5 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-calendar-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">預約類型</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{bookingType === 'ProductTrial' ? '商品試戴' : '聽力檢測'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-calendar-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">預約日期</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{formatDisplayDate(selectedDate)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-time-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">預約時段</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{selectedTime}</p>
                        </div>
                      </div>
                      <div className="w-full h-px bg-[#E8E0D6]" />
                      {hasSelectedProduct && (
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                            <i className="ri-hearing-line text-[#1B3D2A]" />
                          </div>
                          <div>
                            <p className="text-xs text-[#9A8A7A]">來源商品</p>
                            <p className="text-sm font-bold text-[#1B3D2A]">{selectedProduct.name || '已選擇商品'}</p>
                            {selectedProduct.code && <p className="text-xs text-[#9A8A7A]">產品編號：{selectedProduct.code}</p>}
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-user-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">姓名</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{formData.name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-map-pin-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">希望前往門市</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{formData.store}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-phone-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">聯絡電話</p>
                          <p className="text-sm font-bold text-[#1B3D2A]">{formData.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className="ri-file-text-line text-[#1B3D2A]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#9A8A7A]">問題描述</p>
                          <p className="text-sm text-[#5A6B5A] leading-relaxed">{formData.description}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="px-6 py-2.5 rounded-xl text-sm font-medium border border-[#E8E0D6] text-[#5A6B5A] hover:bg-[#FAF8F5] transition-colors flex items-center gap-2"
                      >
                        <i className="ri-arrow-left-line" />
                        修改資料
                      </button>
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-[#1B3D2A] text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            提交中...
                          </>
                        ) : (
                          <>
                            <i className="ri-calendar-check-line" />
                            確認預約
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-xs text-[#9A8A7A] text-center">
                      提交即表示您同意我們的服務條款與隱私政策，您的資料將僅用於預約安排用途。
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-[#FAF8F5] py-6 md:py-8">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionReveal>
              <div className="text-center mb-5 md:mb-6">
                <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                  其他方式
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">其他聯絡方式</h2>
                <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
              </div>
            </SectionReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {contactCards.map((item, idx) => (
                <SectionReveal key={item.title} delay={idx}>
                  <div className="bg-white rounded-2xl border border-[#E8E0D6] p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-300">
                    <div className="w-12 h-12 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center mx-auto">
                      <i className={`${item.icon} text-xl text-[#1B3D2A]`} />
                    </div>
                    <h3 className="mt-3 text-sm font-bold text-[#1B3D2A]">{item.title}</h3>
                    <p className="mt-2 text-sm font-medium text-[#D4A574]">{item.content}</p>
                    {item.sub && <p className="mt-1 text-xs text-[#9A8A7A]">{item.sub}</p>}
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
